import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { authAPI, profileAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  getProfileLocal,
  setProfileLocal,
  clearProfileCache,
} from "../utils/storageHelper";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import LoadingAnimation from "../components/LoadingAnimation";
import { useCleanupOnUnload } from "../hooks/useCleanupOnUnload";

const MAX_ABOUT_ME_LENGTH = 500;
const MAX_PHOTOS = 5;
const MAX_PHOTO_SIZE = 5 * 1024 * 1024; // 5MB

const ETHNICITY_OPTIONS = [
  "Asian",
  "Black",
  "Hispanic",
  "Middle Eastern",
  "Native American",
  "Pacific Islander",
  "South Asian",
  "White",
  "Other",
];

const LANGUAGE_OPTIONS = [
  "English",
  "Spanish",
  "Hindi",
  "French",
  "German",
  "Italian",
  "Arabic",
  "Mandarin",
  "Portuguese",
  "Japanese",
];

const SEXUAL_ORIENTATION_OPTIONS = [
  "straight",
  "gay",
  "lesbian",
  "bisexual",
  "other",
];

const STAR_SIGN_OPTIONS = [
  "Aries",
  "Taurus",
  "Gemini",
  "Cancer",
  "Leo",
  "Virgo",
  "Libra",
  "Scorpio",
  "Sagittarius",
  "Capricorn",
  "Aquarius",
  "Pisces",
];

const EXERCISE_OPTIONS = ["never", "sometimes", "regularly"];
const DRINKING_OPTIONS = ["never", "socially", "often"];
const SMOKING_OPTIONS = ["never", "socially", "often"];
const PET_OPTIONS = ["none", "dog", "cat", "other"];
const EDUCATION_OPTIONS = [
  "high_school",
  "diploma",
  "bachelor",
  "master",
  "phd",
  "other",
];
const MARITAL_STATUS_OPTIONS = ["single", "divorced", "widowed"];
const KIDS_OPTIONS = ["no_kids", "have_kids", "want_kids"];

const normalizeProfile = (data = {}) => {
  return {
    name: data.name || "",
    age: data.age || "",
    gender: data.gender || "",
    lookingFor: data.lookingFor || "",
    bio: data.bio || "",
    aboutMe: data.aboutMe || data.bio || "",
    photos: Array.isArray(data.photos) ? data.photos : [],
    ethnicities: Array.isArray(data.ethnicities) ? data.ethnicities : [],
    nationalities: Array.isArray(data.nationalities) ? data.nationalities : [],
    location: {
      country: data.location?.country || "",
      state: data.location?.state || "",
      city: data.location?.city || "",
    },
    languages: Array.isArray(data.languages) ? data.languages : [],
    height: data.height ?? "",
    weight: data.weight ?? "",
    sexualOrientation: data.sexualOrientation || "",
    starSign: data.starSign || "",
    exercise: data.exercise || "",
    drinking: data.drinking || "",
    smoking: data.smoking || "",
    pets: data.pets || "",
    educationLevel: data.educationLevel || "",
    maritalStatus: data.maritalStatus || "",
    kids: data.kids || "",
    religion: data.religion || "",
  };
};

const formatList = (list) =>
  Array.isArray(list) && list.length > 0 ? list.join(", ") : "Not set";

const formatLocation = (location) => {
  const parts = [location?.city, location?.state, location?.country].filter(
    Boolean,
  );
  return parts.length > 0 ? parts.join(", ") : "Not set";
};

const formatValue = (value) =>
  value === undefined || value === null || value === "" ? "Not set" : value;

const formatNumber = (value, unit) =>
  value === undefined || value === null || value === ""
    ? "Not set"
    : `${value} ${unit}`;

function Profile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [initialProfile, setInitialProfile] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingIdentity, setEditingIdentity] = useState(false);
  const [editingLifestyle, setEditingLifestyle] = useState(false);
  const [tempProfile, setTempProfile] = useState(null);
  const [nationalityInput, setNationalityInput] = useState("");

  const navigate = useNavigate();
  const { deleteAccount } = useAuth();
  const apiCallInProgressRef = useRef(false);
  const initialLoadRef = useRef(false);

  // Clear localStorage when tab is closed
  useCleanupOnUnload();

  const [profile, setProfile] = useState({
    name: "",
    age: "",
    gender: "",
    lookingFor: "",
    bio: "",
    aboutMe: "",
    photos: [],
    ethnicities: [],
    nationalities: [],
    location: { country: "", state: "", city: "" },
    languages: [],
    height: "",
    weight: "",
    sexualOrientation: "",
    starSign: "",
    exercise: "",
    drinking: "",
    smoking: "",
    pets: "",
    educationLevel: "",
    maritalStatus: "",
    kids: "",
    religion: "",
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    if (initialLoadRef.current) return;
    initialLoadRef.current = true;

    const loadProfile = async () => {
      try {
        // Always fetch fresh data from API first to ensure we get latest fields
        if (apiCallInProgressRef.current) return;
        apiCallInProgressRef.current = true;

        console.log("[Profile] Fetching profile from API...");
        const response = await profileAPI.getMyProfile();
        console.log("[Profile] Raw API Response:", response.data);
        console.log("[Profile] Profile Data:", response.data.data);

        const nextProfile = normalizeProfile(response.data.data);
        console.log("[Profile] Normalized Profile:", nextProfile);
        console.log("[Profile] Languages:", nextProfile.languages);
        console.log("[Profile] Ethnicities:", nextProfile.ethnicities);
        console.log("[Profile] Location:", nextProfile.location);
        console.log("[Profile] Exercise:", nextProfile.exercise);

        // Save to localStorage and state
        setProfileLocal(nextProfile);
        setProfile(nextProfile);
        setInitialProfile({
          aboutMe: nextProfile.aboutMe,
          photos: [...nextProfile.photos],
        });
        console.log("[Profile] Profile loaded and set to state");
      } catch (err) {
        console.error("[Profile] Error loading profile:", err);
        console.error("[Profile] Error details:", err.response?.data);
        setError(err.response?.data?.message || "Failed to load profile");
      } finally {
        setLoading(false);
        apiCallInProgressRef.current = false;
      }
    };

    loadProfile();
  }, []);

  useEffect(() => {
    if (!initialProfile) return;
    const aboutMeChanged = profile.aboutMe !== initialProfile.aboutMe;
    const photosChanged =
      profile.photos.length !== initialProfile.photos.length ||
      profile.photos.some(
        (photo, index) => photo !== initialProfile.photos[index],
      );
    setIsDirty(aboutMeChanged || photosChanged);
  }, [profile, initialProfile]);

  const handleAboutMeChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_ABOUT_ME_LENGTH) {
      setProfile((prev) => ({ ...prev, aboutMe: value, bio: value }));
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError("");
    setSuccess("");

    if (!file.type.startsWith("image/")) {
      setError("Only image files are allowed");
      return;
    }

    if (file.size > MAX_PHOTO_SIZE) {
      setError("Image size must be less than 5MB");
      return;
    }

    if (profile.photos.length >= MAX_PHOTOS) {
      setError("Maximum 5 photos allowed");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("photo", file);

      const response = await profileAPI.uploadPhoto(formData);
      const url = response.data.data.url;
      setProfile((prev) => ({ ...prev, photos: [...prev.photos, url] }));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async (photoUrl) => {
    setError("");
    setSuccess("");

    try {
      await profileAPI.deletePhoto(photoUrl);
      setProfile((prev) => ({
        ...prev,
        photos: prev.photos.filter((url) => url !== photoUrl),
      }));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to remove photo");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (profile.aboutMe.length > MAX_ABOUT_ME_LENGTH) {
      setError("About Me must be 500 characters or less");
      return;
    }

    if (profile.photos.length < 1) {
      setError("Please upload at least one photo");
      return;
    }

    // 1. Update localStorage immediately (source of truth)
    setProfileLocal(profile);
    setSuccess("Profile updated successfully");
    setInitialProfile({
      aboutMe: profile.aboutMe,
      photos: [...profile.photos],
    });
    setIsDirty(false);
    console.log("[Profile] Updated localStorage, calling API in background...");

    // 2. Call API in background (user doesn't wait)
    setSaving(true);
    try {
      const response = await profileAPI.createOrUpdate({
        name: profile.name,
        age: profile.age,
        gender: profile.gender,
        lookingFor: profile.lookingFor,
        bio: profile.aboutMe,
        aboutMe: profile.aboutMe,
        photos: profile.photos,
        ethnicities: profile.ethnicities,
        nationalities: profile.nationalities,
        location: profile.location,
        languages: profile.languages,
        height: profile.height,
        weight: profile.weight,
        sexualOrientation: profile.sexualOrientation,
        starSign: profile.starSign,
        exercise: profile.exercise,
        drinking: profile.drinking,
        smoking: profile.smoking,
        pets: profile.pets,
        educationLevel: profile.educationLevel,
        maritalStatus: profile.maritalStatus,
        kids: profile.kids,
        religion: profile.religion,
      });
      // Update with server response
      const normalizedResponse = normalizeProfile(response.data.data);
      setProfileLocal(normalizedResponse);
      setProfile(normalizedResponse);
      console.log("[Profile] API validated, localStorage updated");
    } catch (err) {
      console.error("[Profile] API failed, restoring from localStorage");
      setError(err.response?.data?.message || "Failed to update profile");
      // Rollback: restore from localStorage
      const cached = getProfileLocal();
      if (cached?.data) {
        setProfile(normalizeProfile(cached.data));
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("All fields are required");
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters long");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    setPasswordLoading(true);
    try {
      const response = await authAPI.changePassword({
        currentPassword,
        newPassword,
      });
      setPasswordSuccess(
        response.data?.message || "Password updated successfully",
      );
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      setPasswordError(
        err.response?.data?.message || "Failed to change password",
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSaveIdentity = async () => {
    setError("");
    setSuccess("");
    setSaving(true);

    try {
      console.log("[Profile] Saving identity data:", {
        ethnicities: tempProfile.ethnicities,
        nationalities: tempProfile.nationalities,
        location: tempProfile.location,
        languages: tempProfile.languages,
      });

      // Call API with current profile + temp changes
      const response = await profileAPI.createOrUpdate({
        name: profile.name,
        age: profile.age,
        gender: profile.gender,
        lookingFor: profile.lookingFor,
        bio: profile.aboutMe,
        aboutMe: profile.aboutMe,
        photos: profile.photos,
        ethnicities: tempProfile.ethnicities,
        nationalities: tempProfile.nationalities,
        location: tempProfile.location,
        languages: tempProfile.languages,
        height: profile.height,
        weight: profile.weight,
        sexualOrientation: profile.sexualOrientation,
        starSign: profile.starSign,
        exercise: profile.exercise,
        drinking: profile.drinking,
        smoking: profile.smoking,
        pets: profile.pets,
        educationLevel: profile.educationLevel,
        maritalStatus: profile.maritalStatus,
        kids: profile.kids,
        religion: profile.religion,
      });

      console.log("[Profile] API response after save:", response.data.data);

      // Update state with server response
      const normalizedResponse = normalizeProfile(response.data.data);
      console.log("[Profile] Normalized response:", normalizedResponse);

      setProfile(normalizedResponse);
      setProfileLocal(normalizedResponse);
      setInitialProfile({
        aboutMe: normalizedResponse.aboutMe,
        photos: [...normalizedResponse.photos],
      });

      setSuccess("Identity & Background updated successfully! ✓");
      setEditingIdentity(false);
      setTempProfile(null);
      setNationalityInput("");

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: "smooth" });

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      console.error("[Profile] Save identity error:", err);
      setError(err.response?.data?.message || "Failed to update profile");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLifestyle = async () => {
    setError("");
    setSuccess("");
    setSaving(true);

    try {
      console.log("[Profile] Saving lifestyle data:", {
        height: tempProfile.height,
        weight: tempProfile.weight,
        sexualOrientation: tempProfile.sexualOrientation,
        starSign: tempProfile.starSign,
        exercise: tempProfile.exercise,
        drinking: tempProfile.drinking,
        smoking: tempProfile.smoking,
        pets: tempProfile.pets,
        educationLevel: tempProfile.educationLevel,
        maritalStatus: tempProfile.maritalStatus,
        kids: tempProfile.kids,
        religion: tempProfile.religion,
      });

      // Call API with current profile + temp changes
      const response = await profileAPI.createOrUpdate({
        name: profile.name,
        age: profile.age,
        gender: profile.gender,
        lookingFor: profile.lookingFor,
        bio: profile.aboutMe,
        aboutMe: profile.aboutMe,
        photos: profile.photos,
        ethnicities: profile.ethnicities,
        nationalities: profile.nationalities,
        location: profile.location,
        languages: profile.languages,
        height: tempProfile.height ? parseInt(tempProfile.height) : undefined,
        weight: tempProfile.weight ? parseInt(tempProfile.weight) : undefined,
        sexualOrientation: tempProfile.sexualOrientation,
        starSign: tempProfile.starSign,
        exercise: tempProfile.exercise,
        drinking: tempProfile.drinking,
        smoking: tempProfile.smoking,
        pets: tempProfile.pets,
        educationLevel: tempProfile.educationLevel,
        maritalStatus: tempProfile.maritalStatus,
        kids: tempProfile.kids,
        religion: tempProfile.religion,
      });

      console.log("[Profile] API response after save:", response.data.data);

      // Update state with server response
      const normalizedResponse = normalizeProfile(response.data.data);
      console.log("[Profile] Normalized response:", normalizedResponse);

      setProfile(normalizedResponse);
      setProfileLocal(normalizedResponse);
      setInitialProfile({
        aboutMe: normalizedResponse.aboutMe,
        photos: [...normalizedResponse.photos],
      });

      setSuccess("Lifestyle & Personal Details updated successfully! ✓");
      setEditingLifestyle(false);
      setTempProfile(null);

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: "smooth" });

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      console.error("[Profile] Save lifestyle error:", err);
      setError(err.response?.data?.message || "Failed to update profile");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProfile = async () => {
    setIsDeleting(true);
    try {
      // Clear cache before deletion
      clearProfileCache();
      const result = await deleteAccount();
      if (result.success) {
        navigate("/login", { replace: true });
      } else {
        alert(result.message || "Failed to delete profile. Please try again.");
        setShowDeleteModal(false);
      }
    } catch (error) {
      alert("Failed to delete profile. Please try again.");
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRefreshProfile = async () => {
    setLoading(true);
    setError("");
    try {
      console.log("[Profile] Manual refresh triggered");
      // Clear cache
      localStorage.removeItem("profile");

      const response = await profileAPI.getMyProfile();
      console.log("[Profile] Refresh - API Response:", response.data.data);

      const nextProfile = normalizeProfile(response.data.data);
      console.log("[Profile] Refresh - Normalized Profile:", nextProfile);

      setProfileLocal(nextProfile);
      setProfile(nextProfile);
      setInitialProfile({
        aboutMe: nextProfile.aboutMe,
        photos: [...nextProfile.photos],
      });
      console.log("[Profile] Profile refreshed successfully");
    } catch (err) {
      console.error("[Profile] Refresh error:", err);
      setError(err.response?.data?.message || "Failed to refresh profile");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingAnimation isVisible={true} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-pink-50 to-purple-50 pt-20">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20">
        {/* Global Success/Error Messages */}
        {(error || success) && (
          <div
            className={`mb-6 rounded-2xl px-6 py-4 text-sm font-medium shadow-lg ${
              error
                ? "bg-red-50 text-red-700 border-2 border-red-200"
                : "bg-green-50 text-green-700 border-2 border-green-200"
            }`}
          >
            {error || success}
          </div>
        )}

        {/* Header with Refresh Button */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
          <button
            onClick={handleRefreshProfile}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white shadow-md hover:shadow-lg border border-indigo-200 text-indigo-600 font-semibold transition"
            disabled={loading}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
            Refresh
          </button>
        </div>

        <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)_320px]">
          {/* LEFT COLUMN */}
          <aside className="hidden lg:flex flex-col gap-6">
            <div className="bg-white rounded-3xl shadow-lg p-6 card-hover border border-indigo-100/60">
              <p className="text-xs uppercase tracking-[0.25em] text-indigo-400">
                Profile
              </p>
              <h1 className="text-2xl font-bold text-gray-900 mt-2">
                {profile.name || "Your Name"}
                {profile.age ? `, ${profile.age}` : ""}
              </h1>
              <p className="text-sm text-gray-500 mt-2">
                {profile.aboutMe || profile.bio
                  ? `${(profile.aboutMe || profile.bio).slice(0, 90)}${
                      (profile.aboutMe || profile.bio).length > 90 ? "..." : ""
                    }`
                  : "Add a short about me to introduce yourself."}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {profile.gender && (
                  <span className="px-3 py-1 rounded-full bg-pink-50 text-pink-600 text-xs font-semibold">
                    {profile.gender}
                  </span>
                )}
                {profile.lookingFor && (
                  <span className="px-3 py-1 rounded-full bg-purple-50 text-purple-600 text-xs font-semibold">
                    Looking for {profile.lookingFor}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() =>
                  document
                    .getElementById("profile-edit")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
                className="btn-primary mt-6 w-full py-3 rounded-2xl bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold"
              >
                Edit Profile
              </button>
            </div>

            <div className="bg-white rounded-3xl shadow-lg p-6 border border-purple-100/60">
              <p className="text-xs uppercase tracking-[0.25em] text-purple-400">
                Settings & Preferences
              </p>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Looking for</span>
                  <span className="font-semibold text-gray-900">
                    {formatValue(profile.lookingFor)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Gender</span>
                  <span className="font-semibold text-gray-900">
                    {formatValue(profile.gender)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Age</span>
                  <span className="font-semibold text-gray-900">
                    {formatValue(profile.age)}
                  </span>
                </div>
              </div>
            </div>
          </aside>

          {/* CENTER COLUMN */}
          <section className="space-y-6">
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden ring-1 ring-pink-100/70">
              <div className="relative">
                <div className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                  {(profile.photos.length > 0
                    ? profile.photos
                    : ["https://placehold.co/800x1000?text=Add+Photo"]
                  ).map((photo, index) => (
                    <div
                      key={`${photo}-${index}`}
                      className="min-w-full h-[420px] sm:h-[480px] lg:h-[520px] snap-center"
                    >
                      <img
                        src={photo}
                        alt={`Profile ${index + 1}`}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>

                {profile.photos.length > 1 && (
                  <div className="absolute top-4 inset-x-0 flex justify-center gap-1.5 px-4">
                    {profile.photos.map((_, index) => (
                      <div
                        key={index}
                        className="flex-1 h-1 rounded-full bg-white/30 backdrop-blur-sm max-w-[60px]"
                      />
                    ))}
                  </div>
                )}

                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent text-white p-6 pt-20">
                  <h1 className="text-3xl font-bold">
                    {profile.name || "Your Name"}
                    {profile.age ? `, ${profile.age}` : ""}
                  </h1>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {profile.gender && (
                      <span className="px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-sm font-medium">
                        {profile.gender}
                      </span>
                    )}
                    {profile.lookingFor && (
                      <span className="px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-sm font-medium">
                        Looking for {profile.lookingFor}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile compact details */}
            <div className="lg:hidden bg-white rounded-3xl shadow-lg p-6 border border-indigo-100/60">
              <h2 className="text-lg font-bold text-gray-900">About Me</h2>
              <p className="text-gray-700 leading-relaxed mt-2">
                {profile.aboutMe ||
                  profile.bio ||
                  "Add an about me to tell people about yourself..."}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">
                  {profile.gender || "Gender"}
                </span>
                <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">
                  {profile.lookingFor
                    ? `Looking for ${profile.lookingFor}`
                    : "Preference"}
                </span>
              </div>
            </div>
          </section>

          {/* RIGHT COLUMN */}
          <aside className="hidden lg:flex flex-col gap-6">
            <div className="bg-white rounded-3xl shadow-lg p-6 card-hover border border-pink-100/60">
              <p className="text-xs uppercase tracking-[0.25em] text-pink-400">
                Details
              </p>
              <div className="mt-4 space-y-4">
                <div>
                  <p className="text-xs text-gray-500">Location</p>
                  <p className="text-sm font-semibold text-gray-900 mt-1">
                    {formatLocation(profile.location)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Languages</p>
                  <p className="text-sm font-semibold text-gray-900 mt-1">
                    {formatList(profile.languages)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Ethnicities</p>
                  <p className="text-sm font-semibold text-gray-900 mt-1">
                    {formatList(profile.ethnicities)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Lifestyle</p>
                  <p className="text-sm text-gray-700 mt-1">
                    {[
                      profile.exercise && `Exercise: ${profile.exercise}`,
                      profile.drinking && `Drinking: ${profile.drinking}`,
                      profile.smoking && `Smoking: ${profile.smoking}`,
                      profile.pets && `Pets: ${profile.pets}`,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "Not set"}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl shadow-lg p-6 border border-indigo-100/60">
              {/* <p className="text-xs uppercase tracking-[0.25em] text-indigo-400">
                Actions
              </p> */}
              {/* <div className="mt-4 space-y-3"> */}
              {/* <div className="bg-white rounded-3xl shadow-lg p-6 border-2 border-red-100"> */}
              <h2 className="text-xl font-bold text-gray-900 mb-1">
                Delete Account
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Permanently delete your account and all associated data
              </p>

              <button
                onClick={() => setShowDeleteModal(true)}
                className="w-full py-4 rounded-2xl bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors"
              >
                Delete My Account
              </button>
              {/* </div> */}
              {/* </div> */}
            </div>
          </aside>
        </div>

        {/* Bottom Section */}
        <section className="mt-10 grid gap-6">
          <div className="bg-white rounded-3xl shadow-lg p-6 border border-purple-100/60">
            <h2 className="text-xl font-bold text-gray-900">About Me</h2>
            <p className="text-gray-700 leading-relaxed mt-3">
              {profile.aboutMe ||
                profile.bio ||
                "Add an about me to tell people about yourself..."}
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-lg p-6 border border-pink-100/60">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                Identity & Background
              </h2>
              {!editingIdentity && (
                <button
                  onClick={() => {
                    setTempProfile({ ...profile });
                    setEditingIdentity(true);
                  }}
                  className="px-4 py-2 rounded-xl bg-pink-50 text-pink-600 font-medium hover:bg-pink-100 transition"
                >
                  Edit
                </button>
              )}
            </div>

            {!editingIdentity ? (
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">Location</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatLocation(profile.location)}
                  </p>
                </div>
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">Languages</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatList(profile.languages)}
                  </p>
                </div>
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">Ethnicities</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatList(profile.ethnicities)}
                  </p>
                </div>
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">Nationalities</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatList(profile.nationalities)}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Location */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Location
                  </label>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <input
                      type="text"
                      placeholder="Country"
                      value={tempProfile.location.country}
                      onChange={(e) =>
                        setTempProfile((prev) => ({
                          ...prev,
                          location: {
                            ...prev.location,
                            country: e.target.value,
                          },
                        }))
                      }
                      className="px-4 py-2.5 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none transition"
                    />
                    <input
                      type="text"
                      placeholder="State"
                      value={tempProfile.location.state}
                      onChange={(e) =>
                        setTempProfile((prev) => ({
                          ...prev,
                          location: { ...prev.location, state: e.target.value },
                        }))
                      }
                      className="px-4 py-2.5 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none transition"
                    />
                    <input
                      type="text"
                      placeholder="City"
                      value={tempProfile.location.city}
                      onChange={(e) =>
                        setTempProfile((prev) => ({
                          ...prev,
                          location: { ...prev.location, city: e.target.value },
                        }))
                      }
                      className="px-4 py-2.5 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none transition"
                    />
                  </div>
                </div>

                {/* Languages */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Languages
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {LANGUAGE_OPTIONS.map((lang) => (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => {
                          const isSelected =
                            tempProfile.languages.includes(lang);
                          setTempProfile((prev) => ({
                            ...prev,
                            languages: isSelected
                              ? prev.languages.filter((l) => l !== lang)
                              : [...prev.languages, lang],
                          }));
                        }}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                          tempProfile.languages.includes(lang)
                            ? "bg-pink-500 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {lang}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Ethnicities */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Ethnicities
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {ETHNICITY_OPTIONS.map((eth) => (
                      <button
                        key={eth}
                        type="button"
                        onClick={() => {
                          const isSelected =
                            tempProfile.ethnicities.includes(eth);
                          setTempProfile((prev) => ({
                            ...prev,
                            ethnicities: isSelected
                              ? prev.ethnicities.filter((e) => e !== eth)
                              : [...prev.ethnicities, eth],
                          }));
                        }}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                          tempProfile.ethnicities.includes(eth)
                            ? "bg-pink-500 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {eth}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Nationalities */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nationalities
                  </label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      placeholder="Add nationality"
                      value={nationalityInput}
                      onChange={(e) => setNationalityInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && nationalityInput.trim()) {
                          e.preventDefault();
                          if (
                            !tempProfile.nationalities.includes(
                              nationalityInput.trim(),
                            )
                          ) {
                            setTempProfile((prev) => ({
                              ...prev,
                              nationalities: [
                                ...prev.nationalities,
                                nationalityInput.trim(),
                              ],
                            }));
                          }
                          setNationalityInput("");
                        }
                      }}
                      className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none transition"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          nationalityInput.trim() &&
                          !tempProfile.nationalities.includes(
                            nationalityInput.trim(),
                          )
                        ) {
                          setTempProfile((prev) => ({
                            ...prev,
                            nationalities: [
                              ...prev.nationalities,
                              nationalityInput.trim(),
                            ],
                          }));
                          setNationalityInput("");
                        }
                      }}
                      className="px-6 py-2.5 rounded-xl bg-pink-500 text-white font-medium hover:bg-pink-600 transition"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tempProfile.nationalities.map((nat) => (
                      <div
                        key={nat}
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-pink-500 text-white text-sm font-medium"
                      >
                        {nat}
                        <button
                          type="button"
                          onClick={() =>
                            setTempProfile((prev) => ({
                              ...prev,
                              nationalities: prev.nationalities.filter(
                                (n) => n !== nat,
                              ),
                            }))
                          }
                          className="hover:bg-pink-600 rounded-full p-0.5"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleSaveIdentity}
                    disabled={saving}
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold hover:shadow-lg transition disabled:opacity-50"
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setTempProfile(null);
                      setEditingIdentity(false);
                      setNationalityInput("");
                      setError("");
                    }}
                    disabled={saving}
                    className="px-6 py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-3xl shadow-lg p-6 border border-indigo-100/60">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                Lifestyle & Personal Details
              </h2>
              {!editingLifestyle && (
                <button
                  onClick={() => {
                    setTempProfile({ ...profile });
                    setEditingLifestyle(true);
                  }}
                  className="px-4 py-2 rounded-xl bg-indigo-50 text-indigo-600 font-medium hover:bg-indigo-100 transition"
                >
                  Edit
                </button>
              )}
            </div>

            {!editingLifestyle ? (
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">Exercise</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatValue(profile.exercise)}
                  </p>
                </div>
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">Drinking</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatValue(profile.drinking)}
                  </p>
                </div>
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">Smoking</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatValue(profile.smoking)}
                  </p>
                </div>
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">Pets</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatValue(profile.pets)}
                  </p>
                </div>
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">Height</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatNumber(profile.height, "cm")}
                  </p>
                </div>
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">Weight</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatNumber(profile.weight, "kg")}
                  </p>
                </div>
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">Orientation</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatValue(profile.sexualOrientation)}
                  </p>
                </div>
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">Star Sign</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatValue(profile.starSign)}
                  </p>
                </div>
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">Education</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {profile.educationLevel
                      ? profile.educationLevel.replace(/_/g, " ")
                      : "Not set"}
                  </p>
                </div>
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">Marital Status</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatValue(profile.maritalStatus)}
                  </p>
                </div>
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">Kids</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {profile.kids ? profile.kids.replace(/_/g, " ") : "Not set"}
                  </p>
                </div>
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">Religion</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatValue(profile.religion)}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Exercise */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Exercise
                    </label>
                    <select
                      value={tempProfile.exercise}
                      onChange={(e) =>
                        setTempProfile((prev) => ({
                          ...prev,
                          exercise: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                    >
                      <option value="">Select...</option>
                      {EXERCISE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Drinking */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Drinking
                    </label>
                    <select
                      value={tempProfile.drinking}
                      onChange={(e) =>
                        setTempProfile((prev) => ({
                          ...prev,
                          drinking: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                    >
                      <option value="">Select...</option>
                      {DRINKING_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Smoking */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Smoking
                    </label>
                    <select
                      value={tempProfile.smoking}
                      onChange={(e) =>
                        setTempProfile((prev) => ({
                          ...prev,
                          smoking: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                    >
                      <option value="">Select...</option>
                      {SMOKING_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Pets */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Pets
                    </label>
                    <select
                      value={tempProfile.pets}
                      onChange={(e) =>
                        setTempProfile((prev) => ({
                          ...prev,
                          pets: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                    >
                      <option value="">Select...</option>
                      {PET_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Height */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Height (cm)
                    </label>
                    <input
                      type="number"
                      placeholder="Height in cm"
                      value={tempProfile.height}
                      onChange={(e) =>
                        setTempProfile((prev) => ({
                          ...prev,
                          height: e.target.value,
                        }))
                      }
                      min="100"
                      max="250"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                    />
                  </div>

                  {/* Weight */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Weight (kg)
                    </label>
                    <input
                      type="number"
                      placeholder="Weight in kg"
                      value={tempProfile.weight}
                      onChange={(e) =>
                        setTempProfile((prev) => ({
                          ...prev,
                          weight: e.target.value,
                        }))
                      }
                      min="30"
                      max="200"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                    />
                  </div>

                  {/* Sexual Orientation */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Sexual Orientation
                    </label>
                    <select
                      value={tempProfile.sexualOrientation}
                      onChange={(e) =>
                        setTempProfile((prev) => ({
                          ...prev,
                          sexualOrientation: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                    >
                      <option value="">Select...</option>
                      {SEXUAL_ORIENTATION_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Star Sign */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Star Sign
                    </label>
                    <select
                      value={tempProfile.starSign}
                      onChange={(e) =>
                        setTempProfile((prev) => ({
                          ...prev,
                          starSign: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                    >
                      <option value="">Select...</option>
                      {STAR_SIGN_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Education Level */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Education Level
                    </label>
                    <select
                      value={tempProfile.educationLevel}
                      onChange={(e) =>
                        setTempProfile((prev) => ({
                          ...prev,
                          educationLevel: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                    >
                      <option value="">Select...</option>
                      {EDUCATION_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt.replace(/_/g, " ")}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Marital Status */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Marital Status
                    </label>
                    <select
                      value={tempProfile.maritalStatus}
                      onChange={(e) =>
                        setTempProfile((prev) => ({
                          ...prev,
                          maritalStatus: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                    >
                      <option value="">Select...</option>
                      {MARITAL_STATUS_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Kids */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Kids
                    </label>
                    <select
                      value={tempProfile.kids}
                      onChange={(e) =>
                        setTempProfile((prev) => ({
                          ...prev,
                          kids: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                    >
                      <option value="">Select...</option>
                      {KIDS_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt.replace(/_/g, " ")}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Religion */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Religion
                    </label>
                    <input
                      type="text"
                      placeholder="Your religion"
                      value={tempProfile.religion}
                      onChange={(e) =>
                        setTempProfile((prev) => ({
                          ...prev,
                          religion: e.target.value,
                        }))
                      }
                      maxLength="100"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleSaveLifestyle}
                    disabled={saving}
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold hover:shadow-lg transition disabled:opacity-50"
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setTempProfile(null);
                      setEditingLifestyle(false);
                      setError("");
                    }}
                    disabled={saving}
                    className="px-6 py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Edit & Account Sections */}
        <section id="profile-edit" className="mt-10 space-y-6">
          <div className="bg-white rounded-3xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-1">
              Edit Profile
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Update your photos and about me
            </p>

            {(error || success) && (
              <div
                className={`mb-6 rounded-2xl px-4 py-3 text-sm font-medium ${
                  error
                    ? "bg-red-50 text-red-700 border border-red-100"
                    : "bg-green-50 text-green-700 border border-green-100"
                }`}
              >
                {error || success}
              </div>
            )}

            <form onSubmit={handleSave} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  About Me
                </label>
                <textarea
                  value={profile.aboutMe}
                  onChange={handleAboutMeChange}
                  rows={4}
                  maxLength={MAX_ABOUT_ME_LENGTH}
                  className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
                  placeholder="Tell people about yourself..."
                />
                <div className="text-right text-xs text-gray-400 mt-2">
                  {profile.aboutMe.length}/{MAX_ABOUT_ME_LENGTH}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-semibold text-gray-900">
                    Photos
                  </label>
                  <span className="text-xs font-medium text-gray-500">
                    {profile.photos.length}/{MAX_PHOTOS}
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {profile.photos.map((photo) => (
                    <div
                      key={photo}
                      className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-gray-100 group"
                    >
                      <img
                        src={photo}
                        alt="Profile"
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemovePhoto(photo)}
                        className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                        aria-label="Remove photo"
                      >
                        <span className="w-10 h-10 rounded-full bg-white text-gray-900 flex items-center justify-center text-2xl font-light">
                          ×
                        </span>
                      </button>
                    </div>
                  ))}

                  {profile.photos.length < MAX_PHOTOS && (
                    <label className="aspect-[3/4] rounded-2xl border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-pink-400 hover:bg-pink-50 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        disabled={uploading}
                        className="hidden"
                      />
                      <span className="text-3xl text-gray-400">
                        {uploading ? "..." : "+"}
                      </span>
                    </label>
                  )}
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 mb-3">
                  PROFILE DETAILS
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-xl px-4 py-3">
                    <div className="text-xs text-gray-500 mb-1">Name</div>
                    <div className="text-sm font-medium text-gray-900">
                      {profile.name}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl px-4 py-3">
                    <div className="text-xs text-gray-500 mb-1">Age</div>
                    <div className="text-sm font-medium text-gray-900">
                      {profile.age}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl px-4 py-3">
                    <div className="text-xs text-gray-500 mb-1">Gender</div>
                    <div className="text-sm font-medium text-gray-900">
                      {profile.gender}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl px-4 py-3">
                    <div className="text-xs text-gray-500 mb-1">
                      Looking For
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {profile.lookingFor}
                    </div>
                  </div>
                </div>
              </div>

              {isDirty && (
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold shadow-lg hover:shadow-xl hover:from-pink-600 hover:to-rose-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              )}
            </form>
          </div>

          <div className="bg-white rounded-3xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-1">
              Change Password
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Update your password to keep your account secure
            </p>

            {(passwordError || passwordSuccess) && (
              <div
                className={`mb-6 rounded-2xl px-4 py-3 text-sm font-medium ${
                  passwordError
                    ? "bg-red-50 text-red-700 border border-red-100"
                    : "bg-green-50 text-green-700 border border-green-100"
                }`}
              >
                {passwordError || passwordSuccess}
              </div>
            )}

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordInputChange}
                  className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="Enter current password"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordInputChange}
                  className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="Enter new password"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordInputChange}
                  className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="Re-enter new password"
                />
              </div>

              <button
                type="submit"
                disabled={passwordLoading}
                className="w-full py-4 rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold shadow-lg hover:shadow-xl hover:from-pink-600 hover:to-rose-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {passwordLoading ? "Updating..." : "Update Password"}
              </button>
            </form>
          </div>
        </section>

        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteProfile}
          isDeleting={isDeleting}
        />
      </main>

      <Footer />
    </div>
  );
}

export default Profile;
